import { MenuFoldOutlined, MenuUnfoldOutlined, PlusOutlined } from '@ant-design/icons';
import {
  ThemedTitle,
  useThemedLayoutContext,
  type RefineThemedLayoutSiderProps,
} from '@refinedev/antd';
import { type TreeMenuItem, useLink, useMenu } from '@refinedev/core';
import { Button, ConfigProvider, Drawer, Layout, Menu } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { visibleMenuTree } from './menuTree';
import { SiderProgress } from './SiderProgress';
import { SIDER_EXPAND_KEYS_EVENT } from './siderExpand';
import { readSiderCollapsed, saveSiderCollapsed } from './siderCollapsed';
import type { ItemType } from 'antd/es/menu/interface';
import { SCALE } from '@/config/scale';
import { ROUTES } from '@/constants';
import { getSidebarSetupTourTarget } from '@/features/setup-tours';
import { useThemeMode } from '@/context/themeMode';
import { useIsTouchTier } from '@/hooks/useIsTouchTier';
import { PLATFORM_RESOURCES } from '@/providers/accessControlProvider';
import { isAdminInGlobalView } from '@/services/auth';

const COLLAPSED_WIDTH = 64;
// global.css puts the group guide line under the group icon at this indent.
const INLINE_INDENT = 14;

const isStringArray = (value: unknown): value is Array<string> =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const containsKey = (items: Array<TreeMenuItem>, key: string): boolean =>
  items.some((item) => item.key === key || containsKey(item.children, key));

// The top-level group that holds the current page, or null for a plain row.
const findRouteGroupKey = (tree: Array<TreeMenuItem>, selectedKey: string): string | null =>
  tree.find((item) => item.children.length > 0 && containsKey(item.children, selectedKey))?.key ??
  null;

const SiderContent = ({
  Title: TitleFromProps,
  meta,
}: RefineThemedLayoutSiderProps): React.JSX.Element => {
  const {
    siderCollapsed,
    setSiderCollapsed,
    mobileSiderOpen: isMobileSiderOpen,
    setMobileSiderOpen,
  } = useThemedLayoutContext();
  const { direction } = useContext(ConfigProvider.ConfigContext);
  const Link = useLink();
  const navigate = useNavigate();
  const { menuItems, selectedKey } = useMenu({ meta });
  const { resolvedMode } = useThemeMode();
  const isMobile = useIsTouchTier();
  const isGlobalView = isAdminInGlobalView();
  const isCollapsed = !isMobile && siderCollapsed;
  const RenderToTitle = TitleFromProps ?? ThemedTitle;

  // Global view has no Rental: only the platform screens can work.
  const tree = visibleMenuTree(menuItems).filter(({ name }) =>
    isGlobalView ? PLATFORM_RESOURCES.has(name) : true,
  );

  // One group open at a time: the group of the current page, unless the user opens another.
  // From selectedKey, not useMenu's defaultOpenKeys, which Refine computes on mount only.
  const routeGroupKey = findRouteGroupKey(tree, selectedKey);
  const [openKeys, setOpenKeys] = useState<Array<string>>(routeGroupKey ? [routeGroupKey] : []);
  // The collapsed strip shows a group as a pop-up, which opens on hover, apart from openKeys.
  const [popupKeys, setPopupKeys] = useState<Array<string>>([]);

  useEffect(() => {
    if (routeGroupKey) setOpenKeys([routeGroupKey]);
  }, [routeGroupKey]);

  // A setup tour points at a page inside a group: open that group and the whole sidebar.
  useEffect(() => {
    const handleExpandKeys = (event: Event): void => {
      if (!(event instanceof CustomEvent)) return;
      const keys: unknown = event.detail;
      if (!isStringArray(keys) || keys.length === 0) return;
      setOpenKeys(keys.slice(-1));
      // The tour opens the sidebar for now only: keep the user's saved choice.
      const savedCollapsed = readSiderCollapsed();
      setSiderCollapsed(false);
      saveSiderCollapsed(savedCollapsed);
    };

    window.addEventListener(SIDER_EXPAND_KEYS_EVENT, handleExpandKeys);
    return () => {
      window.removeEventListener(SIDER_EXPAND_KEYS_EVENT, handleExpandKeys);
    };
  }, [setSiderCollapsed]);

  const closeDrawer = (): void => {
    setMobileSiderOpen(false);
  };

  const toMenuItems = (items: Array<TreeMenuItem>): Array<ItemType> =>
    items.map((item) => {
      const label = item.label ?? item.name;
      if (item.children.length > 0) {
        return {
          key: item.key,
          icon: item.icon,
          popupClassName: 'app-sider__popup',
          label: <span>{label}</span>,
          children: toMenuItems(item.children),
        };
      }
      return {
        key: item.key,
        icon: item.icon,
        label: (
          <Link to={item.route ?? ''} data-tour={getSidebarSetupTourTarget(item.route)}>
            {label}
          </Link>
        ),
      };
    });

  const handleOpenChange = (keys: Array<string>): void => {
    if (isCollapsed) {
      setPopupKeys(keys);
      return;
    }
    setOpenKeys(keys.filter((key) => !openKeys.includes(key)).slice(-1));
  };

  const renderSiderBody = (): React.JSX.Element => (
    <>
      <div className="app-sider__brand">
        <RenderToTitle collapsed={isCollapsed} />
      </div>
      {isGlobalView ? null : (
        <div className="app-sider__cta">
          <Button
            type="primary"
            block
            icon={<PlusOutlined />}
            aria-label="New reservation"
            title={isCollapsed ? 'New reservation' : undefined}
            onClick={() => {
              closeDrawer();
              void navigate(ROUTES.RESERVATIONS_CREATE);
            }}
          >
            {isCollapsed ? null : 'New reservation'}
          </Button>
        </div>
      )}
      <Menu
        theme={resolvedMode}
        className="app-sider__menu"
        mode="inline"
        inlineIndent={INLINE_INDENT}
        items={toMenuItems(tree)}
        selectedKeys={selectedKey ? [selectedKey] : []}
        openKeys={isCollapsed ? popupKeys : openKeys}
        onOpenChange={handleOpenChange}
        onClick={closeDrawer}
      />
      <div className="app-sider__foot">
        {isGlobalView ? null : <SiderProgress isCollapsed={isCollapsed} onNavigate={closeDrawer} />}
        {isMobile ? null : (
          <Button
            type="text"
            block
            className="app-sider__collapse"
            icon={isCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={() => {
              setPopupKeys([]);
              setSiderCollapsed(!isCollapsed);
            }}
          >
            {isCollapsed ? null : 'Collapse'}
          </Button>
        )}
      </div>
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        open={isMobileSiderOpen}
        onClose={closeDrawer}
        placement={direction === 'rtl' ? 'right' : 'left'}
        closable={false}
        width={SCALE.drawerWidth}
        styles={{ body: { padding: 0 } }}
        maskClosable={true}
      >
        <Layout.Sider className="app-sider app-sider--drawer" width={SCALE.drawerWidth}>
          {renderSiderBody()}
        </Layout.Sider>
      </Drawer>
    );
  }

  return (
    <Layout.Sider
      className="app-sider"
      width={SCALE.siderWidth}
      collapsedWidth={COLLAPSED_WIDTH}
      collapsed={isCollapsed}
      trigger={null}
    >
      {renderSiderBody()}
    </Layout.Sider>
  );
};

// Wrapper avoids running hooks during ThemedLayout's "hasSider" check.
export const Sider = (props: RefineThemedLayoutSiderProps): React.JSX.Element => (
  <SiderContent {...props} />
);
